import posthog from 'posthog-js';

/**
 * PostHog analytics — consent-gated install (mirrors the marketing site).
 *
 * Hard requirement: NOTHING ph_* in cookies OR localStorage before Cookiebot
 * `statistics` consent. posthog.init() writes its persistence cookie
 * immediately — opt_out_capturing only suppresses event *capture*, not the
 * cookie. So we DEFER posthog.init() entirely until statistics consent is
 * granted, and tear the ph_* cookie + localStorage down again on withdrawal.
 *
 * The Cookiebot CMP + Google Consent Mode live in index.html; this module
 * bridges Cookiebot's statistics consent to PostHog's lifecycle.
 *
 * Every exported call is a safe no-op until PostHog is initialised — analytics
 * must never throw and never block app boot or the auth flow.
 */

/** Fallback EU host so a missing VITE_POSTHOG_HOST never routes to the US region. */
const POSTHOG_EU_HOST = 'https://eu.i.posthog.com';

declare global {
  interface Window {
    Cookiebot?: {
      /**
       * `true` once Cookiebot has resolved an explicit user choice —
       * either freshly from the banner OR by restoring the stored
       * CookieConsent cookie (returning visitor, same registrable
       * domain). `false`/`undefined` while Cookiebot is still booting:
       * `consent` can already be a non-empty object in that window
       * (defaults with `necessary: true`), so `consent` existence is
       * NOT a safe proxy for "we have an answer" — use `hasResponse`.
       */
      hasResponse?: boolean;
      consent?: {
        necessary?: boolean;
        preferences?: boolean;
        statistics?: boolean;
        marketing?: boolean;
      };
    };
  }
}

let apiKey: string | undefined;
let apiHost: string = POSTHOG_EU_HOST;
let initialized = false;
// Injected at bootstrap (DI) so this module imports nothing — keeps it a leaf
// util and avoids a posthog<->authStore import cycle. Read at consent-grant
// time. `email` is needed so we can tag an authenticated internal user with
// `is_internal: true` right after `posthog.identify()`.
let getIdentity: () => AnalyticsIdentity | undefined = () => undefined;

/**
 * Shape passed through `initAnalytics` DI. The posthog module imports no app
 * code; this interface is the only coupling with the auth store.
 */
export interface AnalyticsIdentity {
  readonly id: string;
  readonly email: string;
}

/**
 * Internal-traffic allowlist (Sebastian ruling 2026-10-08).
 *
 * Any authenticated user whose email ends with `@dreamable.kids` is tagged
 * with the `is_internal: true` person property on identify. Case-insensitive;
 * domain match only — no personal email addresses in code.
 *
 * Non-authenticated internal browsing (Sebastian's own devices before login,
 * local dev, booth) can set the same mark via the `?dreamable_internal=1`
 * URL switch below, which registers `is_internal: true` as a super property
 * so every event carries it.
 *
 * Events are STILL SENT (needed for Live Events debugging); they are only
 * filterable out of insights via a project-side filter.
 */
const INTERNAL_EMAIL_DOMAIN_SUFFIX = '@dreamable.kids';
const INTERNAL_URL_PARAM = 'dreamable_internal';
const INTERNAL_LOCALSTORAGE_KEY = 'dreamable_internal';

function isInternalEmail(email: string | undefined): boolean {
  if (!email) return false;
  return email.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN_SUFFIX);
}

function readInternalFlagFromStorage(): boolean {
  try {
    return window.localStorage.getItem(INTERNAL_LOCALSTORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Read `?dreamable_internal=1` → write localStorage flag; `?dreamable_internal=0`
 * → clear it. Any other value or absence → no change. All storage access is
 * try/catch'd (Safari Private Mode throws on setItem).
 */
function syncInternalFlagFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(INTERNAL_URL_PARAM);
    if (raw === '1') {
      window.localStorage.setItem(INTERNAL_LOCALSTORAGE_KEY, '1');
    } else if (raw === '0') {
      window.localStorage.removeItem(INTERNAL_LOCALSTORAGE_KEY);
    }
  } catch {
    // best-effort — URL parse / storage access failures must not throw.
  }
}

// UTM person-attribution: captured from the URL at app load and held in memory
// ONLY (no pre-consent storage), then applied as person properties once PostHog
// initialises on statistics consent. Covers direct-to-app UTM links.
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
let pendingUtms: Record<string, string> | null = null;

/**
 * Sebastian ruling 2026-10-08 — channel super properties for the
 * anonymous book funnel. Stored UTMs reach person properties on
 * signup today (via `applyUtmPersonProperties`), but the pre-auth
 * book funnel (`PersonenRoute`, `BookRoute` with
 * `person_profiles: 'identified_only'`) carries no channel on its
 * events. Registering the UTMs + fbclid presence as super properties
 * attaches them to every subsequent event, including anonymous ones.
 *
 * First-touch semantics match `captureUtmsFromUrl` in utm.ts: a tab
 * captures the UTM state present at bootstrap and carries it for the
 * session. A fresh tab reads whatever UTMs are on its own URL.
 *
 * The raw fbclid VALUE never leaves the parser — only the boolean
 * `has_fbclid: true` reaches super properties.
 */
let pendingHasFbclid: boolean = false;

/**
 * Remove every ph_* cookie (including the cross-subdomain one on
 * .dreamable.kids) and every ph_* localStorage key, so nothing lingers after a
 * consent withdrawal. Best-effort — never throws.
 */
function clearPosthogStorage(): void {
  try {
    const host = window.location.hostname;
    const parts = host.split('.');
    // cross_subdomain_cookie writes to the registrable domain (.dreamable.kids).
    const rootDomain = parts.length > 2 ? '.' + parts.slice(-2).join('.') : host;
    const expired = 'Thu, 01 Jan 1970 00:00:00 GMT';
    for (const entry of document.cookie ? document.cookie.split(';') : []) {
      const name = (entry.split('=')[0] ?? '').trim();
      if (!name.startsWith('ph_')) continue;
      document.cookie = `${name}=; expires=${expired}; path=/`;
      document.cookie = `${name}=; expires=${expired}; path=/; domain=${rootDomain}`;
      document.cookie = `${name}=; expires=${expired}; path=/; domain=${host}`;
    }
  } catch {
    // best-effort cookie teardown
  }
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith('ph_')) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // best-effort localStorage teardown
  }
}

/**
 * Map a backend User.id (uuid) to the SAME distinct_id the backend posthog-node
 * client uses (`user_<uuid>`, see posthog.service.ts). Unifying the format makes
 * anonymous + client + server events resolve to ONE PostHog person, so the
 * funnel spans the whole journey instead of splitting on a bare-uuid vs
 * user_-prefixed person.
 */
function toDistinctId(userId: string): string {
  return `user_${userId}`;
}

/** Identify the currently authenticated user, if any (e.g. consent granted mid-session). */
function identifyCurrentUser(): void {
  const identity = getIdentity();
  if (!identity) return;
  posthog.identify(toDistinctId(identity.id));
  if (isInternalEmail(identity.email)) {
    posthog.setPersonProperties({ is_internal: true });
  }
}

/** Read utm_* params from the current URL. Returns present ones, or null if none. Memory only. */
function readUtmsFromUrl(): Record<string, string> | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const utms: Record<string, string> = {};
    for (const key of UTM_PARAMS) {
      const value = params.get(key);
      if (value) utms[key] = value;
    }
    return Object.keys(utms).length > 0 ? utms : null;
  } catch {
    return null;
  }
}

/**
 * Returns true iff the current URL carries a non-empty `fbclid` query
 * parameter. Memory only, boolean only — the raw value NEVER leaves
 * this function. Facebook click identifiers can be used to re-identify
 * a browser across sessions, so storing or forwarding the value is a
 * privacy ask we do not want to carry.
 */
function readHasFbclidFromUrl(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('fbclid');
    return raw !== null && raw.length > 0;
  } catch {
    return false;
  }
}

/**
 * Apply the held UTMs as person properties: initial-touch via $set_once (the
 * first source sticks) and latest-touch via $set (latest_* keys). No-op when no
 * UTMs were present at load.
 */
function applyUtmPersonProperties(): void {
  if (!pendingUtms) return;
  try {
    const latest: Record<string, string> = {};
    for (const [k, v] of Object.entries(pendingUtms)) latest[`latest_${k}`] = v;
    // setPersonProperties(setProps [$set, latest], setOnceProps [$set_once, initial]).
    posthog.setPersonProperties(latest, pendingUtms);
  } catch {
    // best-effort — attribution must never throw
  }
}

/**
 * Register channel attribution as super properties (Sebastian ruling
 * 2026-10-08 Part E) so every subsequent event — including anonymous
 * pre-registration book-funnel events on an `identified_only` person
 * profile — carries utm_source / utm_medium / utm_campaign /
 * utm_content and `has_fbclid: true` when fbclid was on the landing
 * URL. Only the keys that are set reach `posthog.register`; absent
 * UTMs are not registered (otherwise PostHog's super-property
 * registry would be polluted with `undefined` keys).
 */
function applyChannelSuperProperties(): void {
  const props: Record<string, string | boolean> = {};
  if (pendingUtms) {
    for (const [k, v] of Object.entries(pendingUtms)) {
      props[k] = v;
    }
  }
  if (pendingHasFbclid) {
    props.has_fbclid = true;
  }
  if (Object.keys(props).length === 0) return;
  try {
    posthog.register(props);
  } catch {
    // best-effort — attribution must never throw
  }
}

/**
 * Statistics granted: init PostHog on the first grant (capturing is on by
 * default since init now only happens post-consent), or opt back in on a later
 * re-grant. Then identify the current user if one is already signed in.
 *
 * Idempotency guard (Sebastian ruling 2026-10-08) — Cookiebot fires several
 * events on a single pageload (bootstrap sync when consent is already stored
 * + `CookiebotOnConsentReady` + optionally `CookiebotOnAccept`). Pre-guard,
 * every post-init call re-invoked `opt_in_capturing()` (which emits a `$opt_in`
 * event by default) and re-ran `applyUtmPersonProperties` / `identifyCurrentUser`
 * — producing N `$opt_in` events per pageview. The guard below checks the
 * SDK's actual opted-in state via `has_opted_in_capturing()` and bails if
 * nothing changed. First grant still runs the init path (SDK's own init
 * emits exactly one `$opt_in` internally); a real re-grant after a
 * withdrawal still runs the opt-in path once.
 */
function handleStatisticsGranted(): void {
  if (!apiKey) return;
  if (!initialized) {
    posthog.init(apiKey, {
      api_host: apiHost,
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      capture_pageview: true,
    });
    initialized = true;
    // Internal-traffic super property (Sebastian ruling 2026-10-08) — when
    // the `?dreamable_internal=1` switch has been hit on this device,
    // every event carries `is_internal: true` so the project-side
    // Internal-and-test-accounts filter picks it up.
    if (readInternalFlagFromStorage()) {
      posthog.register({ is_internal: true });
    }
    // Channel attribution super properties (Sebastian ruling 2026-10-08
    // Part E) — attach utm_* + has_fbclid to every subsequent event so
    // the anonymous book funnel carries its source. The person-side
    // $set / $set_once already fires below via applyUtmPersonProperties
    // for identified visitors; this covers the pre-identify majority.
    applyChannelSuperProperties();
    applyUtmPersonProperties();
    identifyCurrentUser();
    return;
  }
  if (posthog.has_opted_in_capturing()) return;
  posthog.opt_in_capturing();
  applyUtmPersonProperties();
  identifyCurrentUser();
}

/**
 * Statistics denied/withdrawn: stop capture + reset identity (if PostHog was
 * ever initialised), then remove all ph_* persistence so nothing lingers.
 */
function handleStatisticsDenied(): void {
  if (initialized) {
    posthog.opt_out_capturing();
    posthog.reset();
  }
  clearPosthogStorage();
}

/**
 * Reconcile PostHog with Cookiebot's current `statistics` consent.
 *
 * Hard rule (Sebastian ruling 2026-10-27, cross-domain stitching
 * incident): wipe PostHog storage ONLY on an explicit decline / with-
 * drawal — i.e. Cookiebot has resolved a user choice AND statistics
 * is false. "No response yet" is NOT a decline — it is silence, and
 * silence must leave the shared `.dreamable.kids` ph_ cookie intact
 * so a visitor coming from www.dreamable.kids keeps her distinct_id
 * / device_id on app.dreamable.kids. Previously, `window.Cookiebot.
 * consent` existing was taken as a proxy for "we have an answer";
 * but Cookiebot populates that object with defaults before it has
 * applied the stored CookieConsent cookie, which drove the wipe-
 * then-re-init race that produced a new person on the app.
 */
function syncConsentFromCookiebot(): void {
  const cb = window.Cookiebot;
  if (!cb || cb.hasResponse !== true) return;
  if (cb.consent?.statistics === true) {
    handleStatisticsGranted();
  } else {
    handleStatisticsDenied();
  }
}

/**
 * Set up the consent bridge at app bootstrap. Reads VITE_POSTHOG_API_KEY /
 * VITE_POSTHOG_HOST. If the key is absent, does nothing (dev/preview without
 * keys behave as before). Does NOT init PostHog — that waits for statistics
 * consent, so no ph_* cookie/localStorage exists pre-consent.
 *
 * @param getIdentityFn dependency-injected getter for the current backend
 *   user's id + email (a closure over the auth store), so this module
 *   imports no app code. Returns `undefined` when nobody is signed in.
 */
export function initAnalytics(getIdentityFn: () => AnalyticsIdentity | undefined): void {
  apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
  if (!apiKey) return; // not configured — skip silently
  getIdentity = getIdentityFn;
  apiHost = import.meta.env.VITE_POSTHOG_HOST || POSTHOG_EU_HOST;

  // Internal-traffic URL switch (Sebastian ruling 2026-10-08). Runs BEFORE
  // the consent bridge so a visit to app.dreamable.kids/?dreamable_internal=1
  // has the flag set in localStorage by the time Cookiebot's ConsentReady
  // event fires and `handleStatisticsGranted` registers the super property.
  syncInternalFlagFromUrl();

  // Capture UTMs from the landing URL now (memory only) — applied as person
  // properties once consent grants and PostHog initialises.
  pendingUtms = readUtmsFromUrl();
  // Capture presence of fbclid at bootstrap. BOOLEAN ONLY — the raw
  // identifier NEVER leaves readHasFbclidFromUrl.
  pendingHasFbclid = readHasFbclidFromUrl();

  // Consent bridge: Cookiebot's `statistics` category gates PostHog entirely.
  window.addEventListener('CookiebotOnConsentReady', syncConsentFromCookiebot);
  window.addEventListener('CookiebotOnAccept', syncConsentFromCookiebot);
  window.addEventListener('CookiebotOnDecline', syncConsentFromCookiebot);

  // If Cookiebot has already resolved an explicit response before our
  // bootstrap runs (returning visitor whose stored consent has been
  // applied), reconcile now rather than wait for an event. Gate on
  // `hasResponse`, NOT `consent` existence: see `syncConsentFromCookiebot`
  // comment for why `consent` existence is unsafe here.
  if (window.Cookiebot?.hasResponse) {
    syncConsentFromCookiebot();
  }
}

/**
 * Identify the current user by backend User.id. Tags the person with
 * `is_internal: true` when the email ends with `@dreamable.kids` (Sebastian
 * ruling 2026-10-08). No-op until PostHog is initialised.
 */
export function phIdentify(userId: string, email: string | undefined): void {
  if (!initialized || !userId) return;
  posthog.identify(toDistinctId(userId));
  if (isInternalEmail(email)) {
    posthog.setPersonProperties({ is_internal: true });
  }
}

/** Reset identity on logout. No-op until PostHog is initialised. */
export function phReset(): void {
  if (!initialized) return;
  posthog.reset();
}

/**
 * Capture a funnel event. No-op (returns false) until PostHog is initialised —
 * i.e. until statistics consent is granted — so all funnel emits are
 * consent-gated automatically. Returns true when the event was actually
 * emitted, letting callers dedupe precisely. Never throws.
 */
export function phCapture(event: string, properties?: Record<string, unknown>): boolean {
  if (!initialized) return false;
  try {
    posthog.capture(event, properties);
    return true;
  } catch {
    return false;
  }
}
